import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Article } from '../../types';
import { Typography } from '../atoms';
import { formatDate, stripHtml, truncateText } from '../../utils/formatters';
import { multiLineEllipsis } from '../../styles/mixins';
import LazyImage from '../LazyImage';

interface ArticleCardProps {
  article: Article;
  onHeightChange?: (height: number) => void;
}

const CardLink = styled(Link)`
  text-decoration: none;
  color: ${theme.colors.white};
  height: 100%;
  width: 80%;
  display: block;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 90%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 95%;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: transparent;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  transition: ${theme.transitions.default};
  margin-bottom: ${theme.space.md};
  padding: 0;
  gap: 20px;  
  height: auto;
  position: relative;
  z-index: 1;
  align-items: center;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 15px;
    margin-bottom: ${theme.space.sm};
  }
`;

const CardImageContainer = styled.div`
  flex: 0 0 200px;
  height: 112px; /* 16:9のアスペクト比 (200 × 9/16 = 112.5) */
  margin: 0;
  border-radius: 24px; /* 画像の四隅をより丸く */
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 0 0 150px;
    height: 84px; /* 16:9のアスペクト比 (150 × 9/16 = 84.375) */
    border-radius: 16px; /* モバイル向けに少し小さめの丸み */
  }
`;

const StyledCardLazyImage = styled(LazyImage)`
  width: 100%;
  height: 100%;
  border-radius: 24px;
  
  img {
    object-fit: cover;
    object-position: center;
    border-radius: 24px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: 16px;
    
    img {
      border-radius: 16px;
    }
  }
`;

const CardContent = styled.div`
  flex: 1;
  padding: ${theme.space.sm} 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  width: calc(100% - 200px); /* 画像の幅を除いた残りのスペース */
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.space.xs} 0;
    width: calc(100% - 150px);
    max-width: 100%;
  }
`;

const CardTitle = styled.h3`
  font-size: ${theme.fontSizes.md};
  margin: 0 0 ${theme.space.md} 0;
  color: ${theme.colors.white};
  font-weight: ${theme.fontWeights.bold};
  line-height: 1.4;
  letter-spacing: 0.5px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.sm};
    margin: 0 0 ${theme.space.sm} 0;
  }
`;

const CardDate = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 ${theme.space.xs} 0;
  font-weight: ${theme.fontWeights.normal};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.xs};
  }
`;

const CardExcerpt = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 ${theme.space.sm} 0;
  ${multiLineEllipsis(2)}
  line-height: ${theme.lineHeights.normal};
  opacity: 0.9;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none; /* モバイルでは非表示 */
  }
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${theme.space.xs};
  margin-top: auto;
`;

const CategoryContainer = styled.div`
  display: flex;
  gap: ${theme.space.xs};
  align-items: center;
  flex-wrap: wrap;
`;

const MetaRow = styled.div`
  display: flex;
  gap: ${theme.space.sm};
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.space.xs};
    width: 100%;
    overflow: hidden;
  }
`;

const CardCategory = styled.span`
  background-color: rgba(29, 157, 231, 0.4);
  color: ${theme.colors.white};
  padding: 2px ${theme.space.xs};
  border-radius: 12px;
  font-size: ${theme.fontSizes.xs};
  display: inline-block;
  letter-spacing: 0.3px;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1px 3px;
    font-size: 9px;
    line-height: 1.2;
    height: 16px;
    display: flex;
    align-items: center;
    border-radius: 10px;
  }
`;

const CardSubCategory = styled(CardCategory)`
  background-color: rgba(52, 194, 71, 0.4);
  color: ${theme.colors.white};
`;

const CardTag = styled.span`
  background-color: rgba(255, 99, 71, 0.6);
  color: ${theme.colors.white};
  padding: 2px ${theme.space.xs};
  border-radius: 12px;
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.3px;
  margin-left: 2px;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1px 3px;
    margin-left: 1px;
    margin-bottom: 2px;
    font-size: 9px;
    line-height: 1.2;
    height: 16px;
    display: flex;
    align-items: center;
    border-radius: 10px;
  }
`;

// formatDateを既存の関数から使用
const formatDateSimple = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}.${month}.${day}`;
};

function ArticleCard({ article, onHeightChange }: ArticleCardProps) {
  const { title, excerpt, date, created_at, category, thumbnail, slug, subCategory, tags } = article;
  const imageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // コンポーネントの高さを測定して親コンポーネントに通知
  useEffect(() => {
    if (cardRef.current && onHeightChange) {
      const resizeObserver = new ResizeObserver(entries => {
        const { height } = entries[0].contentRect;
        onHeightChange(height);
      });
      
      resizeObserver.observe(cardRef.current);
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [onHeightChange]);
  

  
  const hasThumbnail = thumbnail && thumbnail.trim() !== '';
  
  // HTMLタグを削除してプレーンテキストを取得
  const cleanTitle = stripHtml(title);
  
  // 日付をフォーマット
  const formattedDate = formatDateSimple(created_at || date);
  
  // スクロールをトップに戻す関数
  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };
  
  return (
    <CardLink to={`/article/${slug}`} onClick={scrollToTop}>
      <div ref={cardRef}>
        <CardContainer>
          <CardImageContainer ref={imageRef}>
            {hasThumbnail && (
              <StyledCardLazyImage 
                src={thumbnail}
                alt={title || 'Article thumbnail'}
                placeholder="📄"
              />
            )}
          </CardImageContainer>
          <CardContent>
            <div>
              <CardDate>
                {formattedDate}
              </CardDate>
              <CardTitle>
                {title ? <span dangerouslySetInnerHTML={{ __html: title }} /> : ''}
              </CardTitle>
              {excerpt && (
                <CardExcerpt>
                  {excerpt}
                </CardExcerpt>
              )}
            </div>
            
            <CardMeta>
              <MetaRow>
                {category && <CardCategory>{category}</CardCategory>}
                {subCategory && subCategory.length > 0 && 
                  subCategory.map((sub, index) => (
                    <CardSubCategory key={index}>{sub}</CardSubCategory>
                  ))
                }
                
                {tags && tags.length > 0 && 
                  tags.slice(0, 3).map((tag, index) => (
                    <CardTag key={index}>{tag}</CardTag>
                  ))
                }
              </MetaRow>
            </CardMeta>
          </CardContent>
        </CardContainer>
      </div>
    </CardLink>
  );
}

export default ArticleCard; 